import { useState, useEffect } from "react";
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger
} from "@/components/ui/tabs";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Checkbox } from "@/components/ui/checkbox";
import { Label } from "@/components/ui/label";
import { Separator } from "@/components/ui/separator";
import {
  useUpdateVendorMutation,
  type Vendor,
  type VendorRequest,
} from "@/api/vendorApi";
import { useListTestsQuery } from "@/api/testApi";
import { useListDrugPanelsQuery } from "@/features/drug-panels/services/drugPanelApi";
import DrugPanelMultiSelect from "@/features/drug-panels/components/DrugPanelMultiSelect";
import ClinicTestsMultiSelect from "@/features/clinics/components/ClinicTestsMultiSelect";
import { toast } from "sonner";
import {
  User,
  MapPin,
  FlaskConical,
  DollarSign,
  Clock,
  ShieldCheck,
  Truck,
  CreditCard,
  X,
  Plus
} from "lucide-react";

interface OperatingHours {
  mon: string;
  tue: string;
  wed: string;
  thu: string;
  fri: string;
  sat: string;
  sun: string;
  [key: string]: string;
}

interface EditVendorProps {
  vendor: Vendor;
  onClose: () => void;
}

export default function EditVendor({ vendor, onClose }: EditVendorProps) {
  const [activeTab, setActiveTab] = useState("general");
  // Parse uniformOperatingHours if it exists
  const parsedOperatingHours = vendor.uniformOperatingHours
    ? (typeof vendor.uniformOperatingHours === 'string'
      ? JSON.parse(vendor.uniformOperatingHours)
      : vendor.uniformOperatingHours)
    : null;

  // Parse uniformTestConfiguration if it exists
  const parsedTestConfig = vendor.uniformTestConfiguration
    ? (typeof vendor.uniformTestConfiguration === 'string'
      ? JSON.parse(vendor.uniformTestConfiguration)
      : vendor.uniformTestConfiguration)
    : [];

  const [formData, setFormData] = useState<VendorRequest>({
    vendorName: vendor.vendorName,
    contactPersonName: vendor.contactPersonName,
    contactEmail: vendor.contactEmail,
    contactPhone: vendor.contactPhone,
    businessAddress: vendor.businessAddress,
    city: vendor.city,
    state: vendor.state,
    zipCode: vendor.zipCode,
    status: vendor.status,
    appointmentRequirement: vendor.appointmentRequirement || "VARIES_BY_CLINIC",
    servicesScope: vendor.servicesScope || "",
    pricingModel: vendor.pricingModel || "LOCATION_SPECIFIC",
    cocRequirement: vendor.cocRequirement || "VARIES_BY_CLINIC",
    uniformOperatingHours: vendor.uniformOperatingHours || undefined,
    uniformTestConfiguration: vendor.uniformTestConfiguration || undefined,
    acceptsMedigenixForm: vendor.acceptsMedigenixForm ?? true,
    resultDeliveryMethod: vendor.resultDeliveryMethod || "EMAIL",
    resultDeliveryDetails: vendor.resultDeliveryDetails || "",
    uniformDrugConfiguration: vendor.uniformDrugConfiguration || undefined,
    marginConfiguration: vendor.marginConfiguration || undefined,
  });

  const [showOperatingHours, setShowOperatingHours] = useState(!!parsedOperatingHours);
  const [operatingHours, setOperatingHours] = useState<OperatingHours>(
    parsedOperatingHours || {
      mon: "09:00-17:00",
      tue: "09:00-17:00",
      wed: "09:00-17:00",
      thu: "09:00-17:00",
      fri: "09:00-17:00",
      sat: "closed",
      sun: "closed",
    }
  );

  // Test Configuration State
  const [testConfigType, setTestConfigType] = useState<"UNIFORM" | "LOCATION_SPECIFIC">("LOCATION_SPECIFIC");
  const [selectedTestIds, setSelectedTestIds] = useState<number[]>([]);

  // Pricing State
  const [testPrices, setTestPrices] = useState<Record<number, string>>({});

  // Collection Fee State
  const [hasCollectionFee, setHasCollectionFee] = useState<boolean>(true);
  const [collectionFeeType, setCollectionFeeType] = useState<"UNIFORM" | "LOCATION_SPECIFIC">("LOCATION_SPECIFIC");
  const [uniformCollectionFee, setUniformCollectionFee] = useState<string>("");

  // Drug Configuration State
  const [drugConfigType, setDrugConfigType] = useState<"UNIFORM" | "LOCATION_SPECIFIC">("LOCATION_SPECIFIC");
  const [selectedDrugPanelIds, setSelectedDrugPanelIds] = useState<number[]>([]);
  const [drugPrices, setDrugPrices] = useState<Record<number, string>>({});
  const [drugAliases, setDrugAliases] = useState<Record<number, string>>({});

  // Margin Configuration State
  const [marginConfigType, setMarginConfigType] = useState<"UNIFORM" | "LOCATION_SPECIFIC">("LOCATION_SPECIFIC");
  const [testMargins, setTestMargins] = useState<Record<number, { type: "PERCENT" | "FIXED", value: string }>>({});
  const [drugMargins, setDrugMargins] = useState<Record<number, { type: "PERCENT" | "FIXED", value: string }>>({});

  // Helper to prevent mouse wheel from changing number input values
  const preventWheelScroll = (e: React.WheelEvent<HTMLInputElement>) => {
    (e.target as HTMLInputElement).blur();
  };

  useEffect(() => {
    // Parse collection fee config
    if (vendor.collectionFeeConfiguration) {
      try {
        const config = typeof vendor.collectionFeeConfiguration === 'string'
          ? JSON.parse(vendor.collectionFeeConfiguration)
          : vendor.collectionFeeConfiguration;

        if (config.type === "NONE") {
          setHasCollectionFee(false);
          setCollectionFeeType("LOCATION_SPECIFIC");
        } else {
          setHasCollectionFee(true);
          setCollectionFeeType(config.type || "LOCATION_SPECIFIC");
          setUniformCollectionFee((config.amount || "").toString());
        }
      } catch (e) {
        console.error("Failed to parse collection fee config", e);
      }
    }

    // Parse margin config
    if (vendor.marginConfiguration) {
      try {
        const config = typeof vendor.marginConfiguration === 'string'
          ? JSON.parse(vendor.marginConfiguration)
          : vendor.marginConfiguration;

        setMarginConfigType(config.type || "LOCATION_SPECIFIC");
        if (config.tests) setTestMargins(config.tests);
        if (config.drugs) setDrugMargins(config.drugs);
      } catch (e) {
        console.error("Failed to parse margin config", e);
      }
    }
  }, [vendor]);

  // Initialize state once when vendor prop is available
  useEffect(() => {
    if (vendor.uniformTestConfiguration && parsedTestConfig) {
      setTestConfigType("UNIFORM");

      if (Array.isArray(parsedTestConfig)) {
        const ids: number[] = [];
        const prices: Record<number, string> = {};

        parsedTestConfig.forEach((item: any) => {
          if (typeof item === 'number') {
            // Legacy format: Array of numbers
            ids.push(item);
          } else if (typeof item === 'object' && item.testId) {
            // New format: Array of { testId, price }
            ids.push(item.testId);
            if (item.price !== undefined) {
              prices[item.testId] = item.price.toString();
            }
          }
        });
        setSelectedTestIds(ids);
        setTestPrices(prices);
      }
    } else {
      setTestConfigType("LOCATION_SPECIFIC");
    }

    // Initialize drug config
    if (vendor.uniformDrugConfiguration) {
      setDrugConfigType("UNIFORM");
      try {
        const parsedDrugConfig = typeof vendor.uniformDrugConfiguration === 'string'
          ? JSON.parse(vendor.uniformDrugConfiguration)
          : vendor.uniformDrugConfiguration;

        if (Array.isArray(parsedDrugConfig)) {
          const ids: number[] = [];
          const prices: Record<number, string> = {};
          const aliases: Record<number, string> = {};

          parsedDrugConfig.forEach((item: any) => {
            if (item.drugPanelId) {
              ids.push(item.drugPanelId);
              if (item.price !== undefined) prices[item.drugPanelId] = item.price.toString();
              if (item.alias) aliases[item.drugPanelId] = item.alias;
            }
          });
          setSelectedDrugPanelIds(ids);
          setDrugPrices(prices);
          setDrugAliases(aliases);
        }
      } catch (e) {
        console.error("Failed to parse drug config", e);
      }
    } else {
      setDrugConfigType("LOCATION_SPECIFIC");
    }
  }, [vendor]);

  const { data: allTestTypes = [] } = useListTestsQuery();
  const { data: drugPanels = [] } = useListDrugPanelsQuery({});
  const [updateVendor, { isLoading }] = useUpdateVendorMutation();

  // Filter out drug panels (stored as test types with category "Substance Abuse Testing")
  const testTypes = allTestTypes.filter(test => test.testCategory !== "Substance Abuse Testing");

  const handlePriceChange = (testId: number, price: string) => {
    setTestPrices({ ...testPrices, [testId]: price });
  };

  const handleDrugPriceChange = (panelId: number, price: string) => {
    setDrugPrices({ ...drugPrices, [panelId]: price });
  };

  const handleDrugAliasChange = (panelId: number, alias: string) => {
    setDrugAliases({ ...drugAliases, [panelId]: alias });
  };

  const handleTestMarginChange = (testId: number, field: "type" | "value", val: string) => {
    const existing = testMargins[testId] || { type: "FIXED", value: "" };
    setTestMargins({ ...testMargins, [testId]: { ...existing, [field]: val } });
  };

  const handleDrugMarginChange = (panelId: number, field: "type" | "value", val: string) => {
    const existing = drugMargins[panelId] || { type: "FIXED", value: "" };
    setDrugMargins({ ...drugMargins, [panelId]: { ...existing, [field]: val } });
  };

  // Parse accountNumbers if it exists
  const parsedAccountNumbers = vendor.accountNumbers
    ? (typeof vendor.accountNumbers === 'string'
      ? JSON.parse(vendor.accountNumbers)
      : vendor.accountNumbers) // Should be string from API but just in case
    : (vendor.accountNumber ? [vendor.accountNumber] : [""]); // Fallback to legacy field

  // Account Numbers State
  const [accountNumList, setAccountNumList] = useState<string[]>(
    Array.isArray(parsedAccountNumbers) ? parsedAccountNumbers : [""]
  );

  useEffect(() => {
    // Re-initialize if vendor changes (e.g. reopen modal)
    const accs = vendor.accountNumbers
      ? JSON.parse(vendor.accountNumbers)
      : (vendor.accountNumber ? [vendor.accountNumber] : [""]);
    setAccountNumList(Array.isArray(accs) ? accs : [""]);
  }, [vendor]);

  const handleAccountNumberChange = (index: number, value: string) => {
    const newList = [...accountNumList];
    newList[index] = value;
    setAccountNumList(newList);
  };

  const addAccountNumber = () => {
    setAccountNumList([...accountNumList, ""]);
  };

  const removeAccountNumber = (index: number) => {
    if (accountNumList.length > 1) {
      setAccountNumList(accountNumList.filter((_, i) => i !== index));
    }
  };

  const validateForm = (): boolean => {
    const newErrors: Record<string, string> = {};

    // Basic required fields
    if (!formData.vendorName?.trim()) {
      newErrors.vendorName = "Vendor name is required";
    }

    if (!formData.contactEmail?.trim()) {
      newErrors.contactEmail = "Contact email is required";
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.contactEmail)) {
      newErrors.contactEmail = "Invalid email format";
    }

    if (!formData.contactPersonName?.trim()) {
      newErrors.contactPersonName = "Contact person name is required";
    }

    if (!formData.businessAddress?.trim()) {
      newErrors.businessAddress = "Business address is required";
    }

    if (!formData.city?.trim()) {
      newErrors.city = "City is required";
    }

    if (!formData.state?.trim()) {
      newErrors.state = "State is required";
    }

    if (!formData.contactPhone?.trim()) {
      newErrors.contactPhone = "Contact phone is required";
    }

    // Account numbers validation
    const validAccountNumbers = accountNumList.filter(acc => acc.trim() !== "");
    if (validAccountNumbers.length === 0) {
      newErrors.accountNumbers = "At least one account number is required";
    }

    // Test configuration validation
    if (testConfigType === "UNIFORM" && selectedTestIds.length > 0) {
      if (formData.pricingModel === "UNIFORM") {
        // Validate that all selected tests have prices
        const missingPrices = selectedTestIds.filter(testId => {
          const price = testPrices[testId];
          return !price || price.trim() === "" || isNaN(parseFloat(price)) || parseFloat(price) <= 0;
        });

        if (missingPrices.length > 0) {
          newErrors.testPricing = `Please provide valid prices for all selected tests. Missing prices for ${missingPrices.length} test${missingPrices.length !== 1 ? 's' : ''}.`;
        }
      }
    }

    // Drug configuration validation
    if (drugConfigType === "UNIFORM" && selectedDrugPanelIds.length > 0) {
      // Validate that all selected drug panels have prices
      const missingDrugPrices = selectedDrugPanelIds.filter(panelId => {
        const price = drugPrices[panelId];
        return !price || price.trim() === "" || isNaN(parseFloat(price)) || parseFloat(price) <= 0;
      });

      if (missingDrugPrices.length > 0) {
        newErrors.drugPricing = `Please provide valid prices for all selected drug panels. Missing prices for ${missingDrugPrices.length} panel${missingDrugPrices.length !== 1 ? 's' : ''}.`;
      }
    }

    // Margin configuration validation
    if (marginConfigType === "UNIFORM") {
      // Validate test margins when tests are selected
      if (selectedTestIds.length > 0) {
        const missingTestMargins = selectedTestIds.filter(testId => {
          const margin = testMargins[testId];
          return !margin || margin.value === undefined || margin.value === null ||
            margin.value === "" || isNaN(parseFloat(margin.value)) ||
            parseFloat(margin.value) < 0;
        });

        if (missingTestMargins.length > 0) {
          newErrors.testMargins = `Please provide valid margins for all selected tests. Missing margins for ${missingTestMargins.length} test${missingTestMargins.length !== 1 ? 's' : ''}.`;
        }
      }

      // Validate drug margins when drug panels are selected
      if (selectedDrugPanelIds.length > 0) {
        const missingDrugMargins = selectedDrugPanelIds.filter(panelId => {
          const margin = drugMargins[panelId];
          return !margin || margin.value === undefined || margin.value === null ||
            margin.value === "" || isNaN(parseFloat(margin.value)) ||
            parseFloat(margin.value) < 0;
        });

        if (missingDrugMargins.length > 0) {
          newErrors.drugMargins = `Please provide valid margins for all selected drug panels. Missing margins for ${missingDrugMargins.length} panel${missingDrugMargins.length !== 1 ? 's' : ''}.`;
        }
      }
    }

    // Collection fee validation
    if (hasCollectionFee && collectionFeeType === "UNIFORM") {
      if (!uniformCollectionFee || uniformCollectionFee.trim() === "" || isNaN(parseFloat(uniformCollectionFee)) || parseFloat(uniformCollectionFee) < 0) {
        newErrors.collectionFee = "Please provide a valid collection fee amount";
      }
    }

    // Operating hours validation
    if (showOperatingHours) {
      const requiredDays: (keyof OperatingHours)[] = ['mon', 'tue', 'wed', 'thu', 'fri', 'sat', 'sun'];
      const missingHours = requiredDays.filter(day => !operatingHours[day] || operatingHours[day].trim() === "");

      if (missingHours.length > 0) {
        newErrors.operatingHours = `Please provide operating hours for all days. Missing hours for: ${missingHours.join(', ')}`;
      }
    }

    // Result delivery validation
    if (formData.resultDeliveryMethod !== "VENDOR_PORTAL") {
      if (!formData.resultDeliveryDetails?.trim()) {
        newErrors.resultDeliveryDetails = `Please provide ${formData.resultDeliveryMethod === "EMAIL" ? "email address" : "fax number"} for result delivery`;
      } else if (formData.resultDeliveryMethod === "EMAIL" && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.resultDeliveryDetails)) {
        newErrors.resultDeliveryDetails = "Invalid email format for result delivery";
      }
    }

    // If there are any errors, show them and return false
    if (Object.keys(newErrors).length > 0) {
      // Convert errors to user-friendly messages
      const errorMessages = Object.values(newErrors);
      toast.error(`Please fix the following errors:\n\n${errorMessages.join('\n')}`);
      return false;
    }

    return true;
  };

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    try {
      // Prepare uniformTestConfiguration
      let uniformConfig = undefined;
      if (testConfigType === "UNIFORM") {
        if (formData.pricingModel === "UNIFORM") {
          // Store as Array<{ testId: number, price: number }>
          uniformConfig = JSON.stringify(
            selectedTestIds.map(id => ({
              testId: id,
              price: parseFloat(testPrices[id]) || 0
            }))
          );
        } else {
          uniformConfig = JSON.stringify(selectedTestIds);
        }
      }

      // Filter empty account numbers
      const validAccountNumbers = accountNumList.filter(acc => acc.trim() !== "");

      const dataToSubmit: VendorRequest = {
        ...formData,
        uniformOperatingHours: showOperatingHours ? JSON.stringify(operatingHours) : undefined,
        servicesScope: testConfigType,
        uniformTestConfiguration: uniformConfig,
        accountNumbers: validAccountNumbers.length > 0 ? JSON.stringify(validAccountNumbers) : undefined,
        collectionFeeConfiguration: hasCollectionFee ? JSON.stringify({
          type: collectionFeeType,
          amount: collectionFeeType === "UNIFORM" ? (parseFloat(uniformCollectionFee) || 0) : undefined
        }) : JSON.stringify({ type: "NONE" }),
        uniformDrugConfiguration: drugConfigType === "UNIFORM" ? JSON.stringify(
          selectedDrugPanelIds.map(id => ({
            drugPanelId: id,
            price: parseFloat(drugPrices[id]) || 0,
            alias: drugAliases[id] || ""
          }))
        ) : undefined,
        marginConfiguration: JSON.stringify({
          type: marginConfigType,
          tests: marginConfigType === "UNIFORM" ? testMargins : undefined,
          drugs: marginConfigType === "UNIFORM" ? drugMargins : undefined
        }),
      };

      await updateVendor({ id: vendor.id, data: dataToSubmit }).unwrap();
      toast.success("Vendor updated successfully");
      onClose();
    } catch (error) {
      console.error("Update vendor error:", error);
      toast.error("Failed to update vendor. Please try again.");
    }
  };

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>
  ) => {
    const { name, value } = e.target;
    setFormData((prev: any) => ({ ...prev, [name]: value }));
  };

  const handleOperatingHoursChange = (day: keyof OperatingHours, value: string) => {
    setOperatingHours((prev: any) => ({ ...prev, [day]: value }));
  };

  return (
    <div className="flex flex-col h-[85vh]">
      <div className="p-6 border-b bg-muted/20">
        <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
          <TabsList className="grid grid-cols-4 w-full max-w-2xl mx-auto">
            <TabsTrigger value="general" className="gap-2">
              <User className="h-4 w-4" /> <span className="hidden sm:inline">General</span>
            </TabsTrigger>
            <TabsTrigger value="services" className="gap-2">
              <FlaskConical className="h-4 w-4" /> <span className="hidden sm:inline">Services</span>
            </TabsTrigger>
            <TabsTrigger value="pricing" className="gap-2">
              <DollarSign className="h-4 w-4" /> <span className="hidden sm:inline">Pricing</span>
            </TabsTrigger>
            <TabsTrigger value="operations" className="gap-2">
              <Clock className="h-4 w-4" /> <span className="hidden sm:inline">Operations</span>
            </TabsTrigger>
          </TabsList>
        </Tabs>
      </div>

      <form onSubmit={handleSubmit} className="flex-1 overflow-y-auto">
        <Tabs value={activeTab} className="w-full">
          {/* General Tab */}
          <TabsContent value="general" className="p-6 space-y-6 m-0 outline-none">
            <Card className="border-none shadow-none bg-transparent">
              <CardHeader className="px-0">
                <CardTitle className="text-xl flex items-center gap-2">
                  <User className="h-5 w-5 text-primary" /> Basic Information
                </CardTitle>
                <CardDescription>Primary identification and contact details for the vendor.</CardDescription>
              </CardHeader>
              <CardContent className="px-0 space-y-4">
                <div>
                  <Label htmlFor="vendorName">Vendor Name *</Label>
                  <Input
                    id="vendorName"
                    name="vendorName"
                    placeholder="e.g., Quest Diagnostics"
                    value={formData.vendorName}
                    onChange={handleChange}
                    disabled={isLoading}
                    required
                    className="mt-1.5"
                  />
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="contactPersonName">Contact Person</Label>
                    <Input
                      id="contactPersonName"
                      name="contactPersonName"
                      placeholder="John Smith"
                      value={formData.contactPersonName}
                      onChange={handleChange}
                      disabled={isLoading}
                      className="mt-1.5"
                    />
                  </div>
                  <div>
                    <Label htmlFor="contactPhone">Phone *</Label>
                    <Input
                      id="contactPhone"
                      name="contactPhone"
                      placeholder="+1-212-555-0123"
                      value={formData.contactPhone}
                      onChange={handleChange}
                      disabled={isLoading}
                      required
                      className="mt-1.5"
                    />
                  </div>
                </div>

                <div>
                  <Label htmlFor="contactEmail">Email *</Label>
                  <Input
                    id="contactEmail"
                    name="contactEmail"
                    type="email"
                    placeholder="vendor@example.com"
                    value={formData.contactEmail}
                    onChange={handleChange}
                    disabled={isLoading}
                    required
                    className="mt-1.5"
                  />
                </div>

                <Separator className="my-6" />

                <div className="flex items-center gap-2 text-lg font-semibold mb-4">
                  <MapPin className="h-5 w-5 text-primary" /> Business Location
                </div>

                <div>
                  <Label htmlFor="businessAddress">Address</Label>
                  <Input
                    id="businessAddress"
                    name="businessAddress"
                    placeholder="100 Madison Ave"
                    value={formData.businessAddress}
                    onChange={handleChange}
                    disabled={isLoading}
                    className="mt-1.5"
                  />
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                  <div>
                    <Label htmlFor="city">City *</Label>
                    <Input
                      id="city"
                      name="city"
                      placeholder="New York"
                      value={formData.city}
                      onChange={handleChange}
                      disabled={isLoading}
                      required
                      className="mt-1.5"
                    />
                  </div>
                  <div>
                    <Label htmlFor="state">State *</Label>
                    <Input
                      id="state"
                      name="state"
                      placeholder="NY"
                      value={formData.state}
                      onChange={handleChange}
                      disabled={isLoading}
                      required
                      className="mt-1.5"
                    />
                  </div>
                  <div>
                    <Label htmlFor="zipCode">Zip Code *</Label>
                    <Input
                      id="zipCode"
                      name="zipCode"
                      placeholder="10001"
                      value={formData.zipCode}
                      onChange={handleChange}
                      disabled={isLoading}
                      required
                      className="mt-1.5"
                    />
                  </div>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Services Tab */}
          <TabsContent value="services" className="p-6 space-y-6 m-0 outline-none">
            <Card className="border-none shadow-none bg-transparent">
              <CardHeader className="px-0">
                <CardTitle className="text-xl flex items-center gap-2">
                  <FlaskConical className="h-5 w-5 text-primary" /> Test Configuration
                </CardTitle>
                <CardDescription>Define which tests are available and how appointments are handled.</CardDescription>
              </CardHeader>
              <CardContent className="px-0 space-y-6">
                <div>
                  <Label className="block mb-2">Test Availability Scope</Label>
                  <Select
                    value={testConfigType}
                    onValueChange={(val: any) => setTestConfigType(val)}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select Scope" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="LOCATION_SPECIFIC">Location Specific (Defined per Clinic)</SelectItem>
                      <SelectItem value="UNIFORM">Uniform (Same Tests at All Locations)</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                {testConfigType === "UNIFORM" && (
                  <div className="p-4 bg-muted/30 rounded-lg border border-dashed border-primary/20">
                    <Label className="block mb-2">Select Services/Tests Offered</Label>
                    <ClinicTestsMultiSelect
                      availableTests={testTypes}
                      selectedTestIds={selectedTestIds}
                      onChange={setSelectedTestIds}
                      disabled={isLoading}
                    />
                    <p className="text-xs text-muted-foreground mt-2 italic">
                      ℹ️ These tests will be automatically assigned to all clinics under this vendor.
                    </p>
                  </div>
                )}

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                  <div className="space-y-2">
                    <Label htmlFor="appointmentRequirement">Appointment Requirement</Label>
                    <Select
                      value={formData.appointmentRequirement}
                      onValueChange={(val: any) => setFormData((prev: any) => ({ ...prev, appointmentRequirement: val }))}
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="Select Req" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="REQUIRED_ALL">Required at All Locations</SelectItem>
                        <SelectItem value="NOT_REQUIRED">Not Required (Walk-ins)</SelectItem>
                        <SelectItem value="VARIES_BY_CLINIC">Varies by Clinic</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="cocRequirement">COC Requirement</Label>
                    <Select
                      value={formData.cocRequirement}
                      onValueChange={(val: any) => setFormData((prev: any) => ({ ...prev, cocRequirement: val }))}
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="Select COC Req" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="REQUIRED_ALL">Required at All Locations</SelectItem>
                        <SelectItem value="NOT_REQUIRED">Not Required</SelectItem>
                        <SelectItem value="VARIES_BY_CLINIC">Varies by Clinic</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                <Separator className="my-6" />

                <div className="text-lg font-semibold flex items-center gap-2 mb-4">
                  <ShieldCheck className="h-5 w-5 text-primary" /> Drug Testing Configuration
                </div>

                <div>
                  <Label className="block mb-2">Drug Availability Scope</Label>
                  <Select
                    value={drugConfigType}
                    onValueChange={(val: any) => setDrugConfigType(val)}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select Scope" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="LOCATION_SPECIFIC">Location Specific (Defined per Clinic)</SelectItem>
                      <SelectItem value="UNIFORM">Uniform (Same Panels at All Locations)</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                {drugConfigType === "UNIFORM" && (
                  <div className="p-4 bg-muted/30 rounded-lg border border-dashed border-primary/20">
                    <Label className="block mb-2 font-medium">Select Drug Panels Offered</Label>
                    <DrugPanelMultiSelect
                      availablePanels={drugPanels}
                      selectedPanelIds={selectedDrugPanelIds}
                      onChange={setSelectedDrugPanelIds}
                      disabled={isLoading}
                    />
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Pricing Tab */}
          <TabsContent value="pricing" className="p-6 space-y-6 m-0 outline-none">
            <Card className="border-none shadow-none bg-transparent">
              <CardHeader className="px-0">
                <CardTitle className="text-xl flex items-center gap-2">
                  <DollarSign className="h-5 w-5 text-primary" /> Pricing Strategy
                </CardTitle>
                <CardDescription>Configure how services are priced across different clinic locations.</CardDescription>
              </CardHeader>
              <CardContent className="px-0 space-y-6">
                <div>
                  <Label htmlFor="pricingModel">Pricing Model</Label>
                  <Select
                    value={formData.pricingModel}
                    onValueChange={(val: any) => setFormData((prev: any) => ({ ...prev, pricingModel: val }))}
                  >
                    <SelectTrigger className="mt-1.5">
                      <SelectValue placeholder="Select Model" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="UNIFORM">Uniform (Same at All Locations)</SelectItem>
                      <SelectItem value="LOCATION_SPECIFIC">Location-Specific Pricing</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                {/* Uniform Price Configuration */}
                {testConfigType === "UNIFORM" && formData.pricingModel === "UNIFORM" && selectedTestIds.length > 0 && (
                  <div className="space-y-3">
                    <Label className="block font-semibold text-primary">Uniform Test Prices (Vendor Cost)</Label>
                    <div className="border rounded-xl overflow-hidden bg-background">
                      <table className="w-full text-sm">
                        <thead className="bg-muted font-medium">
                          <tr>
                            <th className="px-4 py-2 text-left font-medium">Test Name</th>
                            <th className="px-4 py-2 text-left w-40 font-medium">Cost ($)</th>
                          </tr>
                        </thead>
                        <tbody className="divide-y">
                          {selectedTestIds.map(testId => {
                            const test = testTypes.find(t => t.id === testId);
                            return (
                              <tr key={testId} className="hover:bg-muted/50 transition-colors">
                                <td className="px-4 py-2">{test?.testName || `Test #${testId}`}</td>
                                <td className="px-4 py-2">
                                  <Input
                                    type="number"
                                    onWheel={preventWheelScroll}
                                    placeholder="0.00"
                                    value={testPrices[testId] || ""}
                                    onChange={(e) => handlePriceChange(testId, e.target.value)}
                                    className="h-8"
                                    required
                                  />
                                </td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    </div>
                  </div>
                )}

                {drugConfigType === "UNIFORM" && selectedDrugPanelIds.length > 0 && (
                  <div className="space-y-3">
                    <Label className="block font-semibold text-primary">Uniform Drug Pricing & Aliases</Label>
                    <div className="border rounded-xl overflow-hidden bg-background">
                      <table className="w-full text-sm">
                        <thead className="bg-muted font-medium">
                          <tr>
                            <th className="px-4 py-2 text-left">Panel Name</th>
                            <th className="px-4 py-2 text-left">Alias Name</th>
                            <th className="px-4 py-2 text-left w-32">Cost ($)</th>
                          </tr>
                        </thead>
                        <tbody className="divide-y">
                          {selectedDrugPanelIds.map(id => {
                            const panel = drugPanels.find(p => p.id === id);
                            return (
                              <tr key={id} className="hover:bg-muted/50 transition-colors">
                                <td className="px-4 py-2">{panel?.panelName || `Panel #${id}`}</td>
                                <td className="px-4 py-2">
                                  <Input
                                    placeholder="e.g., DOT Panel"
                                    value={drugAliases[id] || ""}
                                    onChange={(e) => handleDrugAliasChange(id, e.target.value)}
                                    className="h-8"
                                  />
                                </td>
                                <td className="px-4 py-2">
                                  <Input
                                    type="number"
                                    onWheel={preventWheelScroll}
                                    placeholder="0.00"
                                    value={drugPrices[id] || ""}
                                    onChange={(e) => handleDrugPriceChange(id, e.target.value)}
                                    className="h-8"
                                    required
                                  />
                                </td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    </div>
                  </div>
                )}

                <Separator className="my-6" />

                <div className="space-y-4">
                  <div className="text-lg font-semibold flex items-center gap-2">
                    <DollarSign className="h-5 w-5 text-primary" /> Margin Configuration
                  </div>

                  <div>
                    <Label className="block mb-2">Margin Strategy</Label>
                    <Select
                      value={marginConfigType}
                      onValueChange={(val: any) => setMarginConfigType(val)}
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="Select Strategy" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="UNIFORM">Uniform Specific</SelectItem>
                        <SelectItem value="LOCATION_SPECIFIC">Client Specific</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  {marginConfigType === "UNIFORM" && (
                    <div className="p-4 bg-muted/20 rounded-lg border space-y-4">
                      {/* Test Margins */}
                      {(() => {
                        const testIdsToRender = selectedTestIds;
                        if (testIdsToRender.length === 0) {
                          return (
                            <div className="p-3 text-sm text-muted-foreground italic">
                              No tests selected — choose tests in Test Configuration to configure margins.
                            </div>
                          );
                        }
                        return (
                          <div className="space-y-2">
                            <Label className="text-sm font-medium">Test Margins</Label>
                            <div className="border rounded-md overflow-hidden bg-background">
                              <table className="w-full text-xs">
                                <thead className="bg-muted/50">
                                  <tr>
                                    <th className="px-3 py-2 text-left">Test</th>
                                    <th className="px-3 py-2 text-left w-24">Type</th>
                                    <th className="px-3 py-2 text-left w-24">Value</th>
                                  </tr>
                                </thead>
                                <tbody className="divide-y">
                                  {testIdsToRender.map(id => {
                                    const test = testTypes.find(t => t.id === id);
                                    const margin = testMargins[id] || { type: "FIXED", value: "" };
                                    return (
                                      <tr key={id}>
                                        <td className="px-3 py-2 font-medium">{test?.testName || `Test #${id}`}</td>
                                        <td className="px-3 py-2">
                                          <select
                                            className="w-full text-[10px] border rounded p-1 bg-transparent"
                                            value={margin.type || "FIXED"}
                                            onChange={(e) => handleTestMarginChange(id, "type", e.target.value as any)}
                                          >
                                            <option value="FIXED">$ (Fixed)</option>
                                            <option value="PERCENT">% (Percentage)</option>
                                          </select>
                                        </td>
                                        <td className="px-3 py-2">
                                          <Input
                                            type="number"
                                            className="h-7 text-[10px] px-1"
                                            onWheel={preventWheelScroll}
                                            value={margin.value}
                                            onChange={(e) => handleTestMarginChange(id, "value", e.target.value)}
                                            placeholder="0"
                                          />
                                        </td>
                                      </tr>
                                    );
                                  })}
                                </tbody>
                              </table>
                            </div>
                          </div>
                        );
                      })()}

                      {/* Drug Margins */}
                      {drugConfigType === "UNIFORM" && (() => {
                        const panelIdsToRender = selectedDrugPanelIds;
                        if (panelIdsToRender.length === 0) {
                          return (
                            <div className="p-3 text-sm text-muted-foreground italic">
                              No drug panels selected — choose panels in Drug Testing Configuration to configure margins.
                            </div>
                          );
                        }
                        return (
                          <div className="space-y-2">
                            <Label className="text-sm font-medium">Drug Margins</Label>
                            <div className="border rounded-md overflow-hidden bg-background">
                              <table className="w-full text-xs">
                                <thead className="bg-muted/50">
                                  <tr>
                                    <th className="px-3 py-2 text-left">Panel</th>
                                    <th className="px-3 py-2 text-left w-24">Type</th>
                                    <th className="px-3 py-2 text-left w-24">Value</th>
                                  </tr>
                                </thead>
                                <tbody className="divide-y">
                                  {panelIdsToRender.map(id => {
                                    const panel = drugPanels.find(p => p.id === id);
                                    const margin = drugMargins[id] || { type: "FIXED", value: "" };
                                    return (
                                      <tr key={id}>
                                        <td className="px-3 py-2 font-medium">{panel?.panelName || `Panel #${id}`}</td>
                                        <td className="px-3 py-2">
                                          <select
                                            className="w-full text-[10px] border rounded p-1 bg-transparent"
                                            value={margin.type || "FIXED"}
                                            onChange={(e) => handleDrugMarginChange(id, "type", e.target.value as any)}
                                          >
                                            <option value="FIXED">$ (Fixed)</option>
                                            <option value="PERCENT">% (Percentage)</option>
                                          </select>
                                        </td>
                                        <td className="px-3 py-2">
                                          <Input
                                            type="number"
                                            className="h-7 text-[10px] px-1"
                                            onWheel={preventWheelScroll}
                                            value={margin.value}
                                            onChange={(e) => handleDrugMarginChange(id, "value", e.target.value)}
                                            placeholder="0"
                                          />
                                        </td>
                                      </tr>
                                    );
                                  })}
                                </tbody>
                              </table>
                            </div>
                          </div>
                        );
                      })()}
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Operations Tab */}
          <TabsContent value="operations" className="p-6 space-y-6 m-0 outline-none">
            <Card className="border-none shadow-none bg-transparent">
              <CardHeader className="px-0">
                <CardTitle className="text-xl flex items-center gap-2">
                  <Clock className="h-5 w-5 text-primary" /> Business Operations
                </CardTitle>
                <CardDescription>Manage operating hours, collection fees, and delivery methods.</CardDescription>
              </CardHeader>
              <CardContent className="px-0 space-y-6">
                <div className="space-y-2">
                  <Label className="block">Operating Hours Configuration</Label>
                  <Select
                    value={showOperatingHours ? "UNIFORM" : "LOCATION_SPECIFIC"}
                    onValueChange={(val: any) => setShowOperatingHours(val === "UNIFORM")}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select Model" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="LOCATION_SPECIFIC">Location Specific (Defined per Clinic)</SelectItem>
                      <SelectItem value="UNIFORM">Uniform (Same Hours at All Locations)</SelectItem>
                    </SelectContent>
                  </Select>
                  <p className="text-xs text-muted-foreground italic mt-1">
                    {showOperatingHours ? "ℹ️ These hours will be applied to all clinics." : "ℹ️ You will set hours for each clinic individually."}
                  </p>
                </div>

                {showOperatingHours && (
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 p-4 bg-muted/20 border rounded-xl">
                    {(['mon', 'tue', 'wed', 'thu', 'fri', 'sat', 'sun'] as const).map((day) => (
                      <div key={day} className="flex items-center gap-2 bg-background p-2 rounded border">
                        <Label className="w-12 capitalize text-xs font-bold text-muted-foreground">{day}</Label>
                        <Input
                          type="text"
                          placeholder="09:00-17:00 or 'closed'"
                          value={operatingHours[day] || ""}
                          onChange={(e) => handleOperatingHoursChange(day, e.target.value)}
                          disabled={isLoading}
                          className="h-8 text-xs border-none shadow-none focus-visible:ring-0"
                        />
                      </div>
                    ))}
                  </div>
                )}

                <Separator className="my-6" />

                <div className="space-y-4">
                  <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
                    <div className="space-y-1">
                      <Label className="text-base font-semibold">Collection Fee Configuration</Label>
                      <p className="text-xs text-muted-foreground">Does this vendor apply collection fees for tests?</p>
                    </div>
                    <Select
                      value={hasCollectionFee ? "YES" : "NO"}
                      onValueChange={(val) => setHasCollectionFee(val === "YES")}
                    >
                      <SelectTrigger className="w-full sm:w-[180px]">
                        <SelectValue placeholder="Has Fee?" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="YES">Yes (Applies Fees)</SelectItem>
                        <SelectItem value="NO">No (Hide Fees)</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  {hasCollectionFee && (
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-6 p-4 bg-muted/20 border rounded-xl animate-in fade-in slide-in-from-top-2 duration-300">
                      <div className="space-y-2">
                        <Label className="text-sm font-medium">Fee Application Type</Label>
                        <Select
                          value={collectionFeeType}
                          onValueChange={(val: any) => setCollectionFeeType(val)}
                        >
                          <SelectTrigger>
                            <SelectValue placeholder="Select Strategy" />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="LOCATION_SPECIFIC">Location Specific (Per Clinic)</SelectItem>
                            <SelectItem value="UNIFORM">Uniform (Same Fee Everywhere)</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>

                      {collectionFeeType === "UNIFORM" && (
                        <div className="space-y-2">
                          <Label className="text-sm font-medium">Uniform Collection Fee ($)</Label>
                          <Input
                            type="number"
                            onWheel={preventWheelScroll}
                            placeholder="25.00"
                            value={uniformCollectionFee}
                            onChange={(e) => setUniformCollectionFee(e.target.value)}
                            disabled={isLoading}
                            className="h-10"
                          />
                        </div>
                      )}
                    </div>
                  )}
                </div>

                <Separator className="my-6" />

                <div className="space-y-4">
                  <div className="text-lg font-semibold flex items-center gap-2">
                    <Truck className="h-5 w-5 text-primary" /> Result Delivery
                  </div>

                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="resultDeliveryMethod">Delivery Method</Label>
                      <Select
                        value={formData.resultDeliveryMethod}
                        onValueChange={(val: any) => setFormData((prev: any) => ({ ...prev, resultDeliveryMethod: val }))}
                      >
                        <SelectTrigger>
                          <SelectValue placeholder="Select Method" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="EMAIL">Email</SelectItem>
                          <SelectItem value="FAX">Fax</SelectItem>
                          <SelectItem value="VENDOR_PORTAL">Vendor Portal</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>

                    {formData.resultDeliveryMethod !== "VENDOR_PORTAL" && (
                      <div className="space-y-2">
                        <Label htmlFor="resultDeliveryDetails">
                          {formData.resultDeliveryMethod === "EMAIL" ? "Email Address" : "Fax Number"}
                        </Label>
                        <Input
                          id="resultDeliveryDetails"
                          name="resultDeliveryDetails"
                          placeholder={formData.resultDeliveryMethod === "EMAIL" ? "results@vendor.com" : "+1 555-010-9999"}
                          value={formData.resultDeliveryDetails}
                          onChange={handleChange}
                          disabled={isLoading}
                        />
                      </div>
                    )}
                  </div>

                  <div className="flex items-center space-x-2 pt-2">
                    <Checkbox
                      id="acceptsMedigenixForm"
                      checked={formData.acceptsMedigenixForm}
                      onCheckedChange={(val) => setFormData((prev: any) => ({ ...prev, acceptsMedigenixForm: !!val }))}
                    />
                    <Label htmlFor="acceptsMedigenixForm" className="text-sm font-medium leading-none cursor-pointer">
                      Accepts Medigenix Standard Authorization Form
                    </Label>
                  </div>
                </div>

                <Separator className="my-6" />

                <div className="space-y-4">
                  <div className="text-lg font-semibold flex items-center gap-2">
                    <CreditCard className="h-5 w-5 text-primary" /> Account Details
                  </div>

                  <div className="space-y-3">
                    <Label>Account Numbers</Label>
                    <div className="space-y-2">
                      {accountNumList.map((acc, index) => (
                        <div key={index} className="flex items-center gap-2">
                          <Input
                            placeholder="e.g., ACC-12345"
                            value={acc}
                            onChange={(e) => handleAccountNumberChange(index, e.target.value)}
                            disabled={isLoading}
                            className="bg-muted/10 h-9"
                          />
                          <Button
                            type="button"
                            variant="ghost"
                            size="icon"
                            onClick={() => removeAccountNumber(index)}
                            disabled={isLoading || accountNumList.length === 1}
                            className="h-8 w-8 text-muted-foreground hover:text-destructive"
                          >
                            <X className="h-4 w-4" />
                          </Button>
                        </div>
                      ))}
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        onClick={addAccountNumber}
                        disabled={isLoading}
                        className="w-full border-dashed bg-muted/10 hover:bg-muted/20"
                      >
                        <Plus className="h-4 w-4 mr-2" /> Add Account Number
                      </Button>
                    </div>
                  </div>

                  <div className="space-y-2 pt-2">
                    <Label htmlFor="status">System Status</Label>
                    <Select
                      value={formData.status}
                      onValueChange={(val: any) => setFormData((prev: any) => ({ ...prev, status: val }))}
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="System Status" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="ACTIVE">Active</SelectItem>
                        <SelectItem value="INACTIVE">Inactive</SelectItem>
                        <SelectItem value="SUSPENDED">Suspended</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>

        <div className="p-6 border-t bg-muted/20 flex flex-col sm:flex-row gap-3">
          <div className="flex-1 text-sm text-muted-foreground font-medium flex items-center">
            {isLoading && <span className="animate-pulse">Updating vendor data...</span>}
          </div>
          <div className="flex gap-3">
            <Button
              type="button"
              variant="outline"
              onClick={onClose}
              disabled={isLoading}
              className="w-full sm:w-32"
            >
              Cancel
            </Button>
            <Button
              type="submit"
              disabled={isLoading}
              className="w-full sm:w-48 bg-primary hover:bg-primary/90 shadow-lg shadow-primary/20"
            >
              {isLoading ? "Updating..." : "Update Vendor"}
            </Button>
          </div>
        </div>
      </form>
    </div>
  );
}
